import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { PaginationModeType, PaginationProps } from "./interface";

const Pagination = ({
  total,
  page,
  limit,
  setPage,
  styles = {},
  showOnMobile = false, // Default to false (hidden on mobile) for backward compatibility
  infinite = false,
  paginationMode = PaginationModeType.SERVER,
  dataLength = 0
}: PaginationProps) => {
  const isClientMode = paginationMode === PaginationModeType.CLIENT;
  const noPages = Math.ceil(total / limit);
  // Optional safety if calculate noPages as 0:
  const actualNoPages = infinite ? Infinity : noPages || 1;

  // In client mode we don't know the total; Next is enabled only when the
  // current page is full (dataLength >= limit), meaning there may be more data
  const isNextDisabled = isClientMode
    ? dataLength < limit
    : (infinite && total < limit) || (!infinite && page >= actualNoPages);

  const prePage = () => {
    if (page > 1) {
      setPage(page - 1, false);
    }
  };

  const nextPage = () => {
    if (isNextDisabled) return;

    if (isClientMode || page < actualNoPages) {
      setPage(page + 1, false);
    }
  };

  const pageStart = limit * page - limit + 1;
  const pageEnd = limit * page > total ? total : limit * page;
  const totalResults = total;

  const [isInstallBannerVisible, setIsInstallBannerVisible] = useState(false);

  useEffect(() => {
    // Check if install banner is visible
    const checkBannerVisibility = () => {
      const installBanner = document.querySelector("#a2hs-container");
      setIsInstallBannerVisible(
        installBanner?.classList.contains("flex") || false
      );
    };

    // Check on mount and when DOM changes
    checkBannerVisibility();

    // Use MutationObserver to watch for banner visibility changes
    const installBanner = document.querySelector("#a2hs-container");
    if (installBanner) {
      const observer = new MutationObserver(checkBannerVisibility);
      observer.observe(installBanner, {
        attributes: true,
        attributeFilter: ["class"]
      });

      return () => observer.disconnect();
    }
  }, []);

  const isMobile = window.innerWidth < 640;
  // Add bottom margin on mobile if install banner is visible to push pagination above banner
  const mobileBottomMargin =
    isMobile && isInstallBannerVisible && showOnMobile ? "mb-16" : "";

  return (
    <div
      className={classNames(
        showOnMobile
          ? `${isInstallBannerVisible ? "block mb-12 pb-2" : "block"}`
          : "hidden sm:block",
        mobileBottomMargin
      )}
    >
      <div
        className={classNames(
          "flex flex-col items-center justify-between mx-2 my-2 px-3 py-2 bg-white border-t border-gray-200",
          styles?.mobile || ""
        )}
      >
        <nav
          className="flex items-center justify-between w-full"
          aria-label="Pagination"
        >
          <a
            onClick={prePage}
            className={classNames(
              "relative inline-flex items-center rounded-l-md border border-gray-300 px-2 py-2 text-xs font-medium hover:bg-gray-50 focus:z-20",
              page <= 1
                ? "cursor-not-allowed bg-gray-50 text-gray-400"
                : "cursor-pointer bg-white text-gray-500"
            )}
          >
            <ChevronLeftIcon
              className="h-4 w-4 mr-0 md:mr-2"
              aria-hidden="true"
            />
            <span className="md:block hidden">Previous</span>
          </a>

          {/* <span className="text-md text-gray-500 px-4 py-2">{page}</span> */}
          {!isClientMode && !infinite && totalResults > 0 ? (
            <div>
              <p className="text-xs text-gray-700 word-spacing-2">
                Showing <span className="font-semibold">{pageStart}</span> to{" "}
                <span className="font-semibold">{pageEnd}</span> of{" "}
                <span className="font-semibold">{totalResults}</span> results
              </p>
            </div>
          ) : null}

          <a
            onClick={nextPage}
            className={classNames(
              "relative inline-flex items-center rounded-r-md border border-gray-300  px-2 py-2 text-xs font-medium hover:bg-gray-50 focus:z-20",
              isNextDisabled
                ? "cursor-not-allowed bg-gray-50 text-gray-400"
                : "cursor-pointer bg-white text-gray-500"
            )}
          >
            <span className="hidden md:block">Next</span>
            <ChevronRightIcon
              className="h-4 w-4 disabled:text-gray-200 ml-0 md:ml-2"
              aria-hidden="true"
            />
          </a>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
